import type { ParsedLevel } from '@md/engine';
import type { Graphics } from 'pixi.js';
import { createRandom, hashSeed } from './random';

/**
 * Thèmes de RENDU, un par world (concept §9 « direction artistique »).
 *
 * Pourquoi ici et pas dans `@md/game-magnetic-duo` ? Parce que ce fichier ne
 * contient **aucune logique de jeu** (convention n° 4) : rien de ce qu'il
 * décrit n'est lu par la simulation, le solveur ou le validateur. Ce sont des
 * couleurs et des formes de décor, c'est-à-dire de la PRÉSENTATION, dont le
 * client est le seul consommateur. Le moteur reste générique (il ne connaît
 * que `tile.tint`) et le module de jeu reste du contenu.
 *
 * Le choix se fait sur `level.meta.world`, la seule information de monde que
 * porte un niveau chargé — un niveau MCP d'un monde inconnu, ou un niveau
 * synthétique de test, retombe donc sur `DEFAULT_THEME`, qui EST le thème de
 * l'Usine : l'apparence historique du jeu ne bouge pas d'un pixel.
 *
 * Le décor d'arrière-plan est dessiné une seule fois par niveau, à partir d'un
 * PRNG seedé par l'identifiant du niveau : même niveau, même ciel, toujours.
 */
export interface WorldTheme {
  /** Identifiant du world (`level.meta.world`). */
  readonly id: string;
  /** Couleur du canvas au-delà de la zone jouable (letterbox). */
  readonly canvas: number;
  /** Quadrillage discret par-dessus le fond. */
  readonly gridLine: number;
  /** Liseré supérieur des tuiles pleines (relief). */
  readonly solidRim: number;
  readonly solidRimAlpha: number;
  /** Opacité du remplissage des zones traversables colorées (dégaussage). */
  readonly zoneAlpha: number;
  /**
   * Teintes de présentation substituées, par caractère de tuile.
   *
   * Les tuiles PORTEUSES DE SENS de la légende (`s`, `c`, `+`, `-`, `!`, `~`)
   * n'y figurent pas : leur couleur reste celle du `TILE_LEGEND` du module de
   * jeu, identique dans tous les mondes.
   */
  readonly tileTints: Readonly<Record<string, number>>;
  /** Peint le fond de la zone jouable (aplat ou dégradé), sous le quadrillage. */
  paintBackground(target: Graphics, level: ParsedLevel): void;
  /**
   * Peint le décor d'arrière-plan (lune, étoiles, skyline) PAR-DESSUS le
   * quadrillage : sans cela, la grille barre la lune d'une croix.
   */
  paintScenery(target: Graphics, level: ParsedLevel): void;
}

/** Interpole deux couleurs 0xRRGGBB (`amount` ∈ [0, 1]). */
function mixColor(from: number, to: number, amount: number): number {
  const t = Math.min(1, Math.max(0, amount));
  const r = Math.round(((from >> 16) & 0xff) * (1 - t) + ((to >> 16) & 0xff) * t);
  const g = Math.round(((from >> 8) & 0xff) * (1 - t) + ((to >> 8) & 0xff) * t);
  const b = Math.round((from & 0xff) * (1 - t) + (to & 0xff) * t);
  return (r << 16) | (g << 8) | b;
}

/** Dégradé vertical en bandes (PixiJS ne remplit qu'en aplat). */
function fillVerticalGradient(
  target: Graphics,
  level: ParsedLevel,
  top: number,
  bottom: number,
  bands = 20,
): void {
  const step = level.height / bands;
  for (let i = 0; i < bands; i += 1) {
    // Les bandes se chevauchent d'un centième de tuile : aucun liseré de fond
    // ne transparaît entre deux remplissages.
    target
      .rect(0, i * step - 0.01, level.width, step + 0.02)
      .fill({ color: mixColor(top, bottom, (i + 0.5) / bands) });
  }
}

// --- L'Usine (référence historique) -----------------------------------------

const FACTORY_LEVEL_FILL = 0x0d1220;

export const FACTORY_THEME: WorldTheme = {
  id: 'factory',
  canvas: 0x0a0d14,
  gridLine: 0x161d2e,
  solidRim: 0x3d4a70,
  solidRimAlpha: 0.5,
  zoneAlpha: 0.25,
  tileTints: {},
  paintBackground(target, level) {
    target.rect(0, 0, level.width, level.height).fill({ color: FACTORY_LEVEL_FILL });
  },
  paintScenery() {
    // L'Usine n'a pas de décor d'arrière-plan : son fond est un aplat.
  },
};

// --- Les Toits (nuit urbaine) -----------------------------------------------

/** Haut du ciel : indigo profond, là où vivent la lune et les étoiles. */
const ROOFTOPS_SKY_TOP = 0x241d55;
/** Bas du ciel : presque noir, pour que les toits restent lisibles. */
const ROOFTOPS_SKY_BOTTOM = 0x100c2b;
const ROOFTOPS_STAR = 0xdfe7ff;
const ROOFTOPS_MOON = 0xeef2ff;
const ROOFTOPS_HALO = 0xa9bcff;
const ROOFTOPS_SKYLINE = 0x0b0820;
const ROOFTOPS_WINDOW = 0xffcf80;

/**
 * Décor d'arrière-plan des Toits : lune haute, champ d'étoiles, silhouette de
 * ville. Tout est tiré d'un PRNG seedé par `meta.id` — le ciel d'un niveau est
 * le même à chaque chargement, sur toutes les machines.
 */
function paintRooftopsScenery(target: Graphics, level: ParsedLevel): void {
  const { width, height } = level;
  const random = createRandom(hashSeed(level.meta.id));

  // Halo puis disque : une lune un peu voilée, jamais éblouissante. Le halo est
  // empilé en anneaux fins et très transparents — assez serrés pour que
  // l'escalier de valeurs ne se voie pas.
  const moonX = width * 0.78;
  const moonY = height * 0.15;
  const moonRadius = Math.min(1.3, Math.max(0.65, height * 0.06));
  for (let ring = 12; ring >= 1; ring -= 1) {
    target
      .circle(moonX, moonY, moonRadius * (1 + ring * 0.18))
      .fill({ color: ROOFTOPS_HALO, alpha: 0.02 });
  }
  target.circle(moonX, moonY, moonRadius).fill({ color: ROOFTOPS_MOON, alpha: 0.85 });

  // Étoiles : densité proportionnelle à la surface, cantonnées au ciel haut.
  const stars = Math.round(width * height * 0.1);
  for (let i = 0; i < stars; i += 1) {
    const x = random() * width;
    const y = random() * height * 0.68;
    target
      .circle(x, y, 0.025 + random() * 0.045)
      .fill({ color: ROOFTOPS_STAR, alpha: 0.25 + random() * 0.5 });
  }

  // Skyline : bandes sombres au bas du ciel, quelques fenêtres allumées.
  const baseline = height * 0.86;
  let x = -0.5;
  while (x < width) {
    const w = 1.3 + random() * 2.5;
    const top = Math.max(0, baseline - (0.9 + random() * height * 0.24));
    target.rect(x, top, w, height - top).fill({ color: ROOFTOPS_SKYLINE, alpha: 0.9 });
    const rows = Math.floor((baseline - top) / 0.7);
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < 2; col += 1) {
        if (random() > 0.28) {
          continue;
        }
        target
          .rect(x + 0.35 + col * (w - 0.9), top + 0.35 + row * 0.7, 0.16, 0.22)
          .fill({ color: ROOFTOPS_WINDOW, alpha: 0.3 + random() * 0.3 });
      }
    }
    x += w + 0.18;
  }
}

export const ROOFTOPS_THEME: WorldTheme = {
  id: 'rooftops',
  canvas: 0x06040f,
  gridLine: 0x1d1a3f,
  // Arête de toit éclairée par la lune : c'est elle qui « fait » la nuit.
  solidRim: 0x9fb4e8,
  solidRimAlpha: 0.32,
  // Le ciel est plus clair que le fond de l'Usine : les zones dégaussantes
  // ont besoin d'un peu plus de matière pour rester aussi lisibles.
  zoneAlpha: 0.4,
  tileTints: {
    // Murs : ardoise bleutée plutôt que béton industriel.
    '#': 0x353f60,
    // Plateformes fines : zinc de corniche, plus clair sous la lune.
    _: 0x6b7ba6,
  },
  paintBackground(target, level) {
    fillVerticalGradient(target, level, ROOFTOPS_SKY_TOP, ROOFTOPS_SKY_BOTTOM);
  },
  paintScenery: paintRooftopsScenery,
};

/** Thème de repli : l'Usine, c'est-à-dire l'apparence historique du jeu. */
export const DEFAULT_THEME = FACTORY_THEME;

const THEMES: Readonly<Record<string, WorldTheme>> = Object.freeze({
  [FACTORY_THEME.id]: FACTORY_THEME,
  [ROOFTOPS_THEME.id]: ROOFTOPS_THEME,
});

/** Thème d'un world (`level.meta.world`) ; repli sur `DEFAULT_THEME`. */
export function themeForWorld(world: string): WorldTheme {
  return THEMES[world] ?? DEFAULT_THEME;
}
